import axios, { type AxiosError } from "axios"
import { AUTH_MODE, getAccessToken, triggerRelogin } from "@/auth/store"

/** camelCase → snake_case (nur Objekt-Top-Level-Keys, für Query-Parameter). */
function toSnakeCase(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/**
 * API-Client. Im Dev-Modus (VITE_AUTH_MODE=dev) wird der X-Dev-User-Header
 * gesetzt (Backend AUTH_MODE=dev); im Logto-Betrieb haengt der Request-
 * Interceptor das Bearer-Access-Token an (Muster rmnc-personal), das
 * LogtoAuth.tsx ueber setTokenProvider registriert.
 *
 * paramsSerializer: FastAPI-Query-Parameter heißen snake_case
 * (z. B. `komponente_id`), während dieser Client aus Konsistenz zu den
 * response_model_by_alias=True camelCase-Response-Bodies überall camelCase
 * verwendet (z. B. `komponenteId`). Ohne diese zentrale Übersetzung werden
 * Filter-Query-Parameter vom Backend stillschweigend ignoriert (Pydantic/
 * FastAPI kennt nur den exakten Funktionsparameter-Namen) — betraf vor
 * dieser Korrektur u. a. `anlageId` auf mehreren Listen-Endpoints.
 */
export const api = axios.create({
  // BASE_URL endet mit "/" — Root: "/api/v1", Pfad-Betrieb: "/technik/api/v1"
  baseURL: `${import.meta.env.BASE_URL}api/v1`,
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) => {
      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue
        const snakeKey = toSnakeCase(key)
        if (Array.isArray(value)) {
          for (const v of value) searchParams.append(snakeKey, String(v))
        } else {
          searchParams.append(snakeKey, String(value))
        }
      }
      return searchParams.toString()
    },
  },
})

// Request-Interceptor: Bearer-Token (Logto) bzw. X-Dev-User (Dev-Modus)
api.interceptors.request.use(async (config) => {
  if (AUTH_MODE === "dev") {
    config.headers["X-Dev-User"] = JSON.stringify({
      sub: "dev-admin",
      name: "Dev Admin",
      roles: ["admin"],
    })
    return config
  }
  const token = await getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response-Interceptor: 401 (abgelaufene Session) stoesst im Logto-Modus
// genau EINEN Re-Login-Redirect an (Schleifenschutz in triggerRelogin).
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      triggerRelogin()
    }
    return Promise.reject(error)
  },
)

// --- Maschinen (AP-3.3: Anlagen-/Objekt-/Komponentenhierarchie) -------------

export interface Anlage {
  id: number
  kostenstelleNavision: string | null
  bezeichnung: string
  tagUnit: number | null
  tagUnitBeschreibung: string | null
  prozessart: string | null
  referenzTyp: string | null
  beschreibung: string | null
  aktiv: boolean
}

export type AnlageEingabe = Omit<Anlage, "id" | "aktiv"> & { aktiv?: boolean }

export async function fetchAnlagen(): Promise<Anlage[]> {
  const resp = await api.get<Anlage[]>("/anlagen")
  return resp.data
}

export async function fetchAnlage(id: number): Promise<Anlage> {
  const resp = await api.get<Anlage>(`/anlagen/${id}`)
  return resp.data
}

export async function createAnlage(payload: AnlageEingabe): Promise<Anlage> {
  const resp = await api.post<Anlage>("/anlagen", payload)
  return resp.data
}

export async function updateAnlage(id: number, payload: AnlageEingabe): Promise<Anlage> {
  const resp = await api.put<Anlage>(`/anlagen/${id}`, payload)
  return resp.data
}

export async function deleteAnlage(id: number): Promise<void> {
  await api.delete(`/anlagen/${id}`)
}

export interface HierarchieKomponente {
  id: number
  beschreibung: string
  tagNummer: string | null
  navisionCode: string | null
  gesperrt: boolean
  what3words: string | null
}

export interface HierarchieObjekt {
  id: number
  bezeichnung: string
  objektCode: string | null
  komponenten: HierarchieKomponente[]
}

export interface HierarchieAnlage {
  id: number
  bezeichnung: string
  tagUnit: number | null
  aktiv: boolean
  objekte: HierarchieObjekt[]
  komponenten: HierarchieKomponente[]
}

export async function fetchHierarchie(anlageId: number): Promise<HierarchieAnlage> {
  const resp = await api.get<HierarchieAnlage>(`/anlagen/${anlageId}/hierarchie`)
  return resp.data
}

// --- Struktur-Explorer (GET /struktur/tree, docs/STRUKTUR_TREE_KONZEPT.md) ---

export interface StrukturKomponente {
  id: number
  beschreibung: string
  tagNummer: string | null
  gesperrt: boolean
  /** Worst-of der eigenen Prüfungs-/Wartungs-Ampeln; null = keine Fristen erfasst. */
  ampel: Ampel | null
}

export interface StrukturObjekt {
  id: number
  bezeichnung: string
  objektCode: string | null
  ampel: Ampel | null
  komponenten: StrukturKomponente[]
}

export interface StrukturAnlage {
  id: number
  bezeichnung: string
  tagUnit: number | null
  kostenstelleNavision: string | null
  aktiv: boolean
  ampel: Ampel | null
  objekte: StrukturObjekt[]
  komponenten: StrukturKomponente[]
}

/** Gesamter Strukturbaum (alle Anlagen) in einem Request — bewusst eager
 * (~960 Knoten live), damit die clientseitige Suche sofort filtern kann. */
export async function fetchStrukturTree(): Promise<StrukturAnlage[]> {
  const resp = await api.get<StrukturAnlage[]>("/struktur/tree")
  return resp.data
}

export interface Objekt {
  id: number
  anlageId: number
  bezeichnung: string
  beschreibung: string | null
  lageplanId: number | null
  objektCode: string | null
}

export type ObjektEingabe = Omit<Objekt, "id" | "objektCode">

export async function fetchObjekte(anlageId?: number): Promise<Objekt[]> {
  const resp = await api.get<Objekt[]>("/objekte", { params: { anlageId } })
  return resp.data
}

export async function fetchObjekt(id: number): Promise<Objekt> {
  const resp = await api.get<Objekt>(`/objekte/${id}`)
  return resp.data
}

export async function createObjekt(payload: ObjektEingabe): Promise<Objekt> {
  const resp = await api.post<Objekt>("/objekte", payload)
  return resp.data
}

export async function updateObjekt(id: number, payload: ObjektEingabe): Promise<Objekt> {
  const resp = await api.put<Objekt>(`/objekte/${id}`, payload)
  return resp.data
}

export async function deleteObjekt(id: number): Promise<void> {
  await api.delete(`/objekte/${id}`)
}

export interface Komponente {
  id: number
  anlageId: number
  objektId: number | null
  tagBuchstabeId: number | null
  beschreibung: string
  tagZahl: number | null
  tagNummer: string | null
  navisionCode: string | null
  identifikation: string | null
  erschwerniszulage: string | null
  what3words: string | null
  inVerwendung: boolean
  gesperrt: boolean
}

export type KomponenteEingabe = Omit<
  Komponente,
  "id" | "tagNummer" | "navisionCode" | "inVerwendung" | "gesperrt"
> & { inVerwendung?: boolean; gesperrt?: boolean }

export async function fetchKomponenten(filter?: {
  anlageId?: number
  objektId?: number
}): Promise<Komponente[]> {
  const resp = await api.get<Komponente[]>("/komponenten", { params: filter })
  return resp.data
}

export async function fetchKomponente(id: number): Promise<Komponente> {
  const resp = await api.get<Komponente>(`/komponenten/${id}`)
  return resp.data
}

export async function createKomponente(payload: KomponenteEingabe): Promise<Komponente> {
  const resp = await api.post<Komponente>("/komponenten", payload)
  return resp.data
}

export async function updateKomponente(
  id: number,
  payload: KomponenteEingabe,
): Promise<Komponente> {
  const resp = await api.put<Komponente>(`/komponenten/${id}`, payload)
  return resp.data
}

export async function deleteKomponente(id: number): Promise<void> {
  await api.delete(`/komponenten/${id}`)
}

export interface TagBuchstabe {
  id: number
  tagBuchstabe: string
  navisionSchluessel: number | null
  beschreibungSchluessel: string | null
  beschreibungTagBuchstabe: string | null
}

export async function fetchTagBuchstaben(): Promise<TagBuchstabe[]> {
  const resp = await api.get<TagBuchstabe[]>("/tag-buchstaben")
  return resp.data
}

export interface Lageplan {
  id: number
  anlageId: number
  ebene: number | null
  feld: number | null
  beschreibung: string | null
}

export async function fetchLageplaene(anlageId?: number): Promise<Lageplan[]> {
  const resp = await api.get<Lageplan[]>("/lageplaene", { params: { anlageId } })
  return resp.data
}

// --- Prüfungsarten/Prüfungen (AP-3.4, 02-O/N) -------------------------------

export interface Pruefungsart {
  id: number
  pruefungsart: string
  pruefungsintervallJahre: number | null
  pruefungNach: string | null
  anbieterOrganisationId: string | null
  zustaendigeStelle: string | null
  geplanteDauerStunden: number | null
  anzahlMitarbeiter: number | null
  vorbereitungszeitTage: number | null
  notizen: string | null
}

export type PruefungsartEingabe = Omit<Pruefungsart, "id">

export async function fetchPruefungsarten(): Promise<Pruefungsart[]> {
  const resp = await api.get<Pruefungsart[]>("/pruefungsarten")
  return resp.data
}

export async function createPruefungsart(payload: PruefungsartEingabe): Promise<Pruefungsart> {
  const resp = await api.post<Pruefungsart>("/pruefungsarten", payload)
  return resp.data
}

export async function updatePruefungsart(
  id: number,
  payload: PruefungsartEingabe,
): Promise<Pruefungsart> {
  const resp = await api.put<Pruefungsart>(`/pruefungsarten/${id}`, payload)
  return resp.data
}

export async function deletePruefungsart(id: number): Promise<void> {
  await api.delete(`/pruefungsarten/${id}`)
}

export type Ampel = "gruen" | "orange" | "rot"

export interface Pruefung {
  id: number
  pruefungsartId: number
  komponenteId: number | null
  objektId: number | null
  anlageId: number | null
  pruefungsdatum: string | null
  ablaufdatum: string | null
  bestanden: string | null
  nochGueltig: boolean
  bemerkung: string | null
  protokollPaperlessId: string | null
  ampel: Ampel
}

export type PruefungEingabe = Omit<Pruefung, "id" | "ablaufdatum" | "ampel">

export interface PruefungWartungFilter {
  anlageId?: number
  objektId?: number
  komponenteId?: number
}

export async function fetchPruefungen(filter?: PruefungWartungFilter): Promise<Pruefung[]> {
  const resp = await api.get<Pruefung[]>("/pruefungen", { params: filter })
  return resp.data
}

export async function fetchPruefung(id: number): Promise<Pruefung> {
  const resp = await api.get<Pruefung>(`/pruefungen/${id}`)
  return resp.data
}

export async function createPruefung(payload: PruefungEingabe): Promise<Pruefung> {
  const resp = await api.post<Pruefung>("/pruefungen", payload)
  return resp.data
}

export async function updatePruefung(id: number, payload: PruefungEingabe): Promise<Pruefung> {
  const resp = await api.put<Pruefung>(`/pruefungen/${id}`, payload)
  return resp.data
}

export async function deletePruefung(id: number): Promise<void> {
  await api.delete(`/pruefungen/${id}`)
}

export async function duplizierePruefung(id: number): Promise<Pruefung> {
  const resp = await api.post<Pruefung>(`/pruefungen/${id}/duplizieren`)
  return resp.data
}

export async function erstellePruefungServiceticket(
  id: number,
): Promise<{ id: number; ticketKey: string | null }> {
  const resp = await api.post(`/pruefungen/${id}/servicetickets`)
  return resp.data
}

// --- Wartungsarten/Wartungen (AP-3.4, 02-Y/W) -------------------------------

export interface Wartungsart {
  id: number
  titel: string
  gueltigkeitsdauerJahre: number | null
  gesetzlicheGrundlage: string | null
  artDerTaetigkeit: string | null
  extern: string | null
  anbieterOrganisationId: string | null
  zustaendigeStelle: string | null
  geplanteDauerStunden: number | null
  anzahlMitarbeiter: number | null
  vorbereitungszeitTage: number | null
}

export type WartungsartEingabe = Omit<Wartungsart, "id">

export async function fetchWartungsarten(): Promise<Wartungsart[]> {
  const resp = await api.get<Wartungsart[]>("/wartungsarten")
  return resp.data
}

export async function createWartungsart(payload: WartungsartEingabe): Promise<Wartungsart> {
  const resp = await api.post<Wartungsart>("/wartungsarten", payload)
  return resp.data
}

export async function updateWartungsart(
  id: number,
  payload: WartungsartEingabe,
): Promise<Wartungsart> {
  const resp = await api.put<Wartungsart>(`/wartungsarten/${id}`, payload)
  return resp.data
}

export async function deleteWartungsart(id: number): Promise<void> {
  await api.delete(`/wartungsarten/${id}`)
}

export interface Wartung {
  id: number
  wartungsartId: number
  komponenteId: number | null
  objektId: number | null
  anlageId: number | null
  wartungsdatum: string | null
  ablaufdatum: string | null
  status: string | null
  aktiv: boolean
  ergebnis: string | null
  protokollPaperlessId: string | null
  ampel: Ampel
}

export type WartungEingabe = Omit<Wartung, "id" | "ablaufdatum" | "ampel">

export async function fetchWartungen(filter?: PruefungWartungFilter): Promise<Wartung[]> {
  const resp = await api.get<Wartung[]>("/wartungen", { params: filter })
  return resp.data
}

export async function fetchWartung(id: number): Promise<Wartung> {
  const resp = await api.get<Wartung>(`/wartungen/${id}`)
  return resp.data
}

export async function createWartung(payload: WartungEingabe): Promise<Wartung> {
  const resp = await api.post<Wartung>("/wartungen", payload)
  return resp.data
}

export async function updateWartung(id: number, payload: WartungEingabe): Promise<Wartung> {
  const resp = await api.put<Wartung>(`/wartungen/${id}`, payload)
  return resp.data
}

export async function deleteWartung(id: number): Promise<void> {
  await api.delete(`/wartungen/${id}`)
}

export async function dupliziereWartung(id: number): Promise<Wartung> {
  const resp = await api.post<Wartung>(`/wartungen/${id}/duplizieren`)
  return resp.data
}

export async function erstelleWartungServiceticket(
  id: number,
): Promise<{ id: number; ticketKey: string | null }> {
  const resp = await api.post(`/wartungen/${id}/servicetickets`)
  return resp.data
}

// --- Ersatzteilarten/Ersatzteile (AP-3.5, 02-M/L) ---------------------------

export interface Ersatzteilart {
  id: number
  name: string
  beschreibung: string | null
}

export async function fetchErsatzteilarten(): Promise<Ersatzteilart[]> {
  const resp = await api.get<Ersatzteilart[]>("/ersatzteilarten")
  return resp.data
}

export interface Ersatzteil {
  id: number
  bezeichnung: string
  beschreibung: string | null
  ersatzteilartId: number | null
  lieferant1OrganisationId: string | null
  lieferant2OrganisationId: string | null
  lieferant3OrganisationId: string | null
  spsZuordnung: string | null
  kommunikationsprotokoll: string | null
  messgroesse: string | null
  ersatzWirdVerwendet: boolean
}

export type ErsatzteilEingabe = Omit<Ersatzteil, "id">

export async function fetchErsatzteile(filter?: { komponenteId?: number }): Promise<Ersatzteil[]> {
  const resp = await api.get<Ersatzteil[]>("/ersatzteile", { params: filter })
  return resp.data
}

export async function fetchErsatzteil(id: number): Promise<Ersatzteil> {
  const resp = await api.get<Ersatzteil>(`/ersatzteile/${id}`)
  return resp.data
}

export async function createErsatzteil(payload: ErsatzteilEingabe): Promise<Ersatzteil> {
  const resp = await api.post<Ersatzteil>("/ersatzteile", payload)
  return resp.data
}

export async function updateErsatzteil(
  id: number,
  payload: ErsatzteilEingabe,
): Promise<Ersatzteil> {
  const resp = await api.put<Ersatzteil>(`/ersatzteile/${id}`, payload)
  return resp.data
}

export async function deleteErsatzteil(id: number): Promise<void> {
  await api.delete(`/ersatzteile/${id}`)
}

export interface ErsatzteilLieferant {
  id: string
  name: string
  kuerzel: string | null
  aktiv: boolean
}

export async function fetchErsatzteilLieferanten(id: number): Promise<ErsatzteilLieferant[]> {
  const resp = await api.get<ErsatzteilLieferant[]>(`/ersatzteile/${id}/lieferanten`)
  return resp.data
}

/** 02-C.P2: Komponenten, in denen dieses Ersatzteil verbaut ist. */
export async function fetchErsatzteilKomponenten(id: number): Promise<Komponente[]> {
  const resp = await api.get<Komponente[]>(`/ersatzteile/${id}/komponenten`)
  return resp.data
}

/** Issue #5: Ersatzteil einer Komponente zuordnen (Junction, idempotent). */
export async function addErsatzteilKomponente(
  ersatzteilId: number,
  komponenteId: number,
): Promise<void> {
  await api.post(`/ersatzteile/${ersatzteilId}/komponenten/${komponenteId}`)
}

// --- Bilddokumentation je Anlage (AP-3.5, 02-JB/KB) -------------------------

export interface AnlageBild {
  id: number
  anlageId: number
  bildbeschreibung: string | null
  paperlessId: string
}

export async function fetchAnlageBilder(anlageId: number): Promise<AnlageBild[]> {
  const resp = await api.get<AnlageBild[]>(`/anlagen/${anlageId}/bilder`)
  return resp.data
}

export async function uploadAnlageBild(
  anlageId: number,
  file: File,
  bildbeschreibung?: string,
): Promise<AnlageBild> {
  const formData = new FormData()
  formData.append("file", file)
  const resp = await api.post<AnlageBild>(`/anlagen/${anlageId}/bilder`, formData, {
    params: bildbeschreibung ? { bildbeschreibung } : undefined,
    headers: { "Content-Type": "multipart/form-data" },
  })
  return resp.data
}

export async function deleteAnlageBild(bildId: number): Promise<void> {
  await api.delete(`/anlagen/bilder/${bildId}`)
}

// --- Dashboard-Summary (AP-3.5, Frontend-Zustand statt 02-GB/CB/FB) ---------

export interface DashboardSummary {
  anlagenGesamt: number
  pruefungenRot: number
  pruefungenOrange: number
  wartungenRot: number
  wartungenOrange: number
  ticketsOffen: number
}

export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  const resp = await api.get<DashboardSummary>("/dashboard/summary")
  return resp.data
}

// AP-4.3: G.2-G.5-Äquivalent — Anzahl offener Tickets je Art
export interface TicketArtZahlen {
  wartungInspektion: number
  pruefung: number
  aufgabe: number
  ereignis: number
  auftrag: number
}

export async function fetchTicketsJeArt(): Promise<TicketArtZahlen> {
  const resp = await api.get<TicketArtZahlen>("/dashboard/tickets-je-art")
  return resp.data
}

// --- Tickets (AP-4.1/4.2/4.3) -----------------------------------------------

export type TicketStatus = "neu" | "offen" | "in_arbeit" | "warten" | "erledigt" | "zurueckgewiesen"
export type TicketArt = "wartung_inspektion" | "pruefung" | "aufgabe" | "ereignis" | "auftrag"

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  neu: "Neu",
  offen: "Offen",
  in_arbeit: "In Arbeit",
  warten: "Wartet",
  erledigt: "Erledigt",
  zurueckgewiesen: "Zurückgewiesen",
}

export const TICKET_ART_LABEL: Record<TicketArt, string> = {
  wartung_inspektion: "Wartung/Inspektion",
  pruefung: "Prüfung",
  aufgabe: "Aufgabe",
  ereignis: "Ereignis",
  auftrag: "Auftrag",
}

export interface Ticket {
  id: number
  ticketKey: string | null
  kurzbeschreibung: string
  beschreibung: string | null
  status: TicketStatus
  prioritaet: string | null
  sourceType: string
  sourceId: number | null
  art: TicketArt
  zeitbedarfStunden: number | null
  geplanterStart: string | null
  geplantesEnde: string | null
  spaetesterAnfangstermin: string | null
  tatsaechlicheFertigstellung: string | null
  stoeranalyseNotwendig: string | null
  stoeranalyse: string | null
  unterstuetzungExtern: boolean
  priofolge: number | null
}

export interface TicketMitarbeiterZuordnung {
  id: number
  employeeId: string
  verantwortlich: boolean
  abgeschlossen: boolean
}

export interface TicketLieferantZuordnung {
  id: number
  organisationId: string
}

export interface TicketDetail extends Ticket {
  mitarbeiter: TicketMitarbeiterZuordnung[]
  lieferanten: TicketLieferantZuordnung[]
  alleArbeitenAbgeschlossen: boolean
}

export interface TicketFilter {
  status?: string
  art?: string
  prioritaet?: string
  sourceType?: string
  suche?: string
  meine?: boolean
  komponenteId?: number
}

export async function fetchTickets(filter?: TicketFilter): Promise<Ticket[]> {
  const resp = await api.get<Ticket[]>("/tickets", { params: filter })
  return resp.data
}

export async function fetchTicket(id: number): Promise<TicketDetail> {
  const resp = await api.get<TicketDetail>(`/tickets/${id}`)
  return resp.data
}

export interface TicketEingabe {
  kurzbeschreibung: string
  beschreibung?: string | null
  sourceType?: string
  sourceId?: number | null
  prioritaet?: string | null
}

export async function createTicket(payload: TicketEingabe): Promise<Ticket> {
  const resp = await api.post<Ticket>("/tickets", payload)
  return resp.data
}

export interface TicketUpdateEingabe {
  kurzbeschreibung?: string
  beschreibung?: string | null
  prioritaet?: string | null
  status?: string
  zeitbedarfStunden?: number | null
  geplanterStart?: string | null
  geplantesEnde?: string | null
}

export async function updateTicket(id: number, payload: TicketUpdateEingabe): Promise<Ticket> {
  const resp = await api.put<Ticket>(`/tickets/${id}`, payload)
  return resp.data
}

export async function recalcTicketStartDate(
  id: number,
): Promise<{ id: number; spaetesterAnfangstermin: string | null }> {
  const resp = await api.post(`/tickets/${id}/recalc-start-date`)
  return resp.data
}

export async function zuordneTicketMitarbeiter(
  id: number,
  employeeId: string,
  verantwortlich = false,
): Promise<TicketMitarbeiterZuordnung> {
  const resp = await api.post<TicketMitarbeiterZuordnung>(`/tickets/${id}/mitarbeiter`, {
    employeeId,
    verantwortlich,
  })
  return resp.data
}

export async function entferneTicketMitarbeiter(id: number, employeeId: string): Promise<void> {
  await api.delete(`/tickets/${id}/mitarbeiter/${employeeId}`)
}

export async function zuordneTicketLieferant(
  id: number,
  organisationId: string,
): Promise<TicketLieferantZuordnung> {
  const resp = await api.post<TicketLieferantZuordnung>(`/tickets/${id}/lieferanten`, {
    organisationId,
  })
  return resp.data
}

export async function loescheTicketLieferanten(id: number): Promise<void> {
  await api.delete(`/tickets/${id}/lieferanten`)
}

export async function updateTicketStoeranalyse(
  id: number,
  payload: { stoeranalyseNotwendig?: string | null; stoeranalyse?: string | null },
): Promise<Ticket> {
  const resp = await api.put<Ticket>(`/tickets/${id}/stoeranalyse`, payload)
  return resp.data
}

// --- Ereignisse (AP-4.4/4.5) -------------------------------------------------

export type Ereignistyp = "mangel" | "stoerung" | "missstand"
export type Ereignisstatus = "erledigt" | "unerledigt" | "ohne_serviceticket"

export const EREIGNISTYP_LABEL: Record<Ereignistyp, string> = {
  mangel: "Mangel",
  stoerung: "Störung",
  missstand: "Missstand",
}

export const EREIGNISSTATUS_LABEL: Record<Ereignisstatus, string> = {
  erledigt: "Erledigt",
  unerledigt: "Unerledigt",
  ohne_serviceticket: "Ohne Serviceticket",
}

export interface Ereignis {
  id: number
  kurzbeschreibung: string
  beschreibung: string | null
  ereignistyp: Ereignistyp
  zeitpunkt: string
  melderEmployeeId: string
  anlageId: number
  objektId: number | null
  komponenteId: number | null
  kvpTeilprojektId: number | null
  schichtDatum: string | null
  schichtCode: string | null
  bildPaperlessId: string | null
  ereignisIdAnzeige: string
  ereignisstatus: Ereignisstatus
  anzahlServicetickets: number
}

export interface EreignisEingabe {
  kurzbeschreibung: string
  beschreibung?: string | null
  ereignistyp: Ereignistyp
  zeitpunkt: string
  melderEmployeeId: string
  anlageId: number
  objektId?: number | null
  komponenteId?: number | null
  kvpTeilprojektId?: number | null
  schichtDatum?: string | null
  schichtCode?: string | null
}

export interface EreignisFilter {
  ereignistyp?: string
  anlageId?: number
  von?: string
  bis?: string
  status?: Ereignisstatus
}

export async function fetchEreignisse(filter?: EreignisFilter): Promise<Ereignis[]> {
  const resp = await api.get<Ereignis[]>("/ereignisse", { params: filter })
  return resp.data
}

export async function fetchEreignis(id: number): Promise<Ereignis> {
  const resp = await api.get<Ereignis>(`/ereignisse/${id}`)
  return resp.data
}

export async function createEreignis(payload: EreignisEingabe): Promise<Ereignis> {
  const resp = await api.post<Ereignis>("/ereignisse", payload)
  return resp.data
}

export interface EreignisLookupEintrag {
  id: number
  bezeichnung?: string
  beschreibung?: string
}

export async function fetchEreignisLookupObjekte(anlageId?: number): Promise<EreignisLookupEintrag[]> {
  const resp = await api.get<EreignisLookupEintrag[]>("/ereignisse/lookup/objekte", {
    params: { anlage_id: anlageId },
  })
  return resp.data
}

export async function fetchEreignisLookupKomponenten(
  anlageId?: number,
): Promise<EreignisLookupEintrag[]> {
  const resp = await api.get<EreignisLookupEintrag[]>("/ereignisse/lookup/komponenten", {
    params: { anlage_id: anlageId },
  })
  return resp.data
}

export async function uploadEreignisBild(id: number, file: File): Promise<Ereignis> {
  const formData = new FormData()
  formData.append("file", file)
  const resp = await api.post<Ereignis>(`/ereignisse/${id}/bild`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return resp.data
}

export interface TicketAusEreignis {
  id: number
  ticketKey: string | null
  prioritaet: string | null
}

export async function erstelleTicketAusEreignis(id: number): Promise<TicketAusEreignis> {
  const resp = await api.post<TicketAusEreignis>(`/ereignisse/${id}/servicetickets`)
  return resp.data
}

export interface EreignisKenntnisnahme {
  id: number
  employeeId: string
}

export async function fetchKenntnisnahmen(id: number): Promise<EreignisKenntnisnahme[]> {
  const resp = await api.get<EreignisKenntnisnahme[]>(`/ereignisse/${id}/kenntnisnahmen`)
  return resp.data
}

export async function kenntnisnahmeHinzufuegen(
  id: number,
  employeeId: string,
): Promise<EreignisKenntnisnahme> {
  const resp = await api.post<EreignisKenntnisnahme>(`/ereignisse/${id}/kenntnisnahmen`, {
    employeeId,
  })
  return resp.data
}

// --- Ereignis-Dashboard (AP-4.5, 10-F6) -------------------------------------

export interface EreignisDashboardFilter {
  ereignistyp?: Ereignistyp
  status?: Ereignisstatus
  ohneTicketEinbeziehen?: boolean
  anlageIds?: number[]
  von?: string
  bis?: string
}

export interface ZeitraumErgebnis {
  ereignisseJeTyp: Record<string, number>
  gesamt: number
}

export interface ProzentVergleich {
  zahl1: number
  zahl2: number
  prozent: number | null
  richtung: "gestiegen" | "gesunken" | "gleich" | "neu" | "keine_daten"
}

export interface VergleichsErgebnis {
  zeitraeume: ZeitraumErgebnis[]
  vergleichZeitraumEinsZwei: ProzentVergleich | null
  vergleichZeitraumDreiVier: ProzentVergleich | null
}

export async function fetchEreignisVergleich(
  zeitraeume: EreignisDashboardFilter[],
): Promise<VergleichsErgebnis> {
  const resp = await api.post<VergleichsErgebnis>("/ereignis-dashboard/vergleich", zeitraeume)
  return resp.data
}

export interface ArbeitsortErgebnis {
  anlageId: number
  anlageBezeichnung: string
  anzahl: number
}

export async function fetchEreignisJeArbeitsort(
  filter: EreignisDashboardFilter,
): Promise<ArbeitsortErgebnis[]> {
  const resp = await api.post<ArbeitsortErgebnis[]>("/ereignis-dashboard/je-arbeitsort", filter)
  return resp.data
}

// --- TimeTab (AP-3.7/AP-3.8) -------------------------------------------------

export interface Zeitbuchung {
  id: number
  employeeId: string
  datum: string
  uhrzeit: string | null
  stunden: number
  komponenteId: number
  leistungId: number
  schadenstyp: string | null
  schadenscodeId: number | null
  freitext: string | null
  ticketId: number | null
  gebucht: boolean
  timetabId: string | null
  navisionNr: string | null
  leistungAnzeige: string | null
  serviceticketBeschreibung: string | null
}

export interface ZeitbuchungEingabe {
  employeeId: string
  datum: string
  uhrzeit?: string | null
  stunden: number
  minuten?: number
  komponenteId: number
  leistungId: number
  schadenstyp?: string | null
  schadenscodeId?: number | null
  freitext?: string | null
  ticketId?: number | null
  // AP-4.6/R-F-25: expliziter Nutzerwille statt impliziter Ninox-Tab-Trigger.
  mitarbeiterAbschliessen?: boolean
}

export interface ZeitbuchungenFilter {
  employeeId?: string
  von?: string
  bis?: string
  komponenteId?: number
  ticketId?: number
  gebucht?: boolean
}

export async function fetchZeitbuchungen(filter?: ZeitbuchungenFilter): Promise<Zeitbuchung[]> {
  const resp = await api.get<Zeitbuchung[]>("/zeitbuchungen", { params: filter })
  return resp.data
}

export async function createZeitbuchung(payload: ZeitbuchungEingabe): Promise<Zeitbuchung> {
  const resp = await api.post<Zeitbuchung>("/zeitbuchungen", payload)
  return resp.data
}

export async function toggleBuchung(id: number): Promise<Zeitbuchung> {
  const resp = await api.post<Zeitbuchung>(`/zeitbuchungen/${id}/toggle-buchung`)
  return resp.data
}

export interface TimetabKomponente {
  id: number
  beschreibung: string
  tagNummer: string | null
  navisionCode: string | null
}

export async function fetchTimetabKomponenten(anlageId?: number): Promise<TimetabKomponente[]> {
  const resp = await api.get<TimetabKomponente[]>("/timetab/komponenten", {
    params: { anlage_id: anlageId },
  })
  return resp.data
}

export interface TimetabLeistung {
  id: number
  leistung: string
  kuerzel: string | null
}

export async function fetchTimetabLeistungen(): Promise<TimetabLeistung[]> {
  const resp = await api.get<TimetabLeistung[]>("/timetab/leistungen")
  return resp.data
}

export interface TimetabSchadenscode {
  id: number
  schadensbezeichnung: string
  codetyp: string | null
  schadenscode: string | null
}

export async function fetchTimetabSchadenscodes(
  schadenstyp?: string,
): Promise<TimetabSchadenscode[]> {
  const resp = await api.get<TimetabSchadenscode[]>("/timetab/schadenscodes", {
    params: { schadenstyp },
  })
  return resp.data
}

export interface TicketKontext {
  anlageId: number | null
  komponenteId: number | null
  ticketKey: string | null
  beschreibung: string | null
}

export async function fetchTicketKontext(ticketId: number): Promise<TicketKontext> {
  const resp = await api.get<TicketKontext>(`/timetab/servicetickets/${ticketId}/kontext`)
  return resp.data
}

export function exportBuchhaltungUrl(jahr?: number, monat?: number): string {
  const params = new URLSearchParams()
  if (jahr) params.set("jahr", String(jahr))
  if (monat) params.set("monat", String(monat))
  const query = params.toString()
  return `/api/v1/timetab/export/buchhaltung${query ? `?${query}` : ""}`
}

export function exportErschwerniszulageUrl(jahr?: number, monat?: number): string {
  const params = new URLSearchParams()
  if (jahr) params.set("jahr", String(jahr))
  if (monat) params.set("monat", String(monat))
  const query = params.toString()
  return `/api/v1/timetab/export/erschwerniszulage${query ? `?${query}` : ""}`
}

// --- KVP (AP-4.7/4.8): Projekte → Teilprojekte → Aufgaben (Ninox 05) -------

/** Abgeleiteter Ebenen-Status (Backend leite_status_ab) + Abbruch-Marker. */
export type KvpAbgeleiteterStatus =
  | "neu"
  | "in_arbeit"
  | "abgeschlossen"
  | "verworfen"
  | "abgebrochen"

export interface KvpAbbruchFelder {
  abgebrochenAm: string | null
  abgebrochenVon: string | null
  abbruchGrund: string | null
  statusBeiAbbruch: string | null
}

export interface KvpAufgabenZaehler {
  aufgabenGesamt: number
  aufgabenOffen: number
  aufgabenErledigt: number
  aufgabenVerworfen: number
}

export interface KvpProjekt extends KvpAbbruchFelder, KvpAufgabenZaehler {
  id: number
  businessId: string | null
  beschreibung: string
  art: string
  abgeleiteterStatus: KvpAbgeleiteterStatus
}

export async function fetchKvpProjekte(): Promise<KvpProjekt[]> {
  const resp = await api.get<KvpProjekt[]>("/kvp/projekte")
  return resp.data
}

export async function createKvpProjekt(payload: {
  beschreibung: string
  art?: string
}): Promise<KvpProjekt> {
  const resp = await api.post<KvpProjekt>("/kvp/projekte", payload)
  return resp.data
}

export async function updateKvpProjekt(
  id: number,
  payload: Partial<{ beschreibung: string; art: string }>,
): Promise<KvpProjekt> {
  const resp = await api.put<KvpProjekt>(`/kvp/projekte/${id}`, payload)
  return resp.data
}

export interface KvpTeilprojekt extends KvpAbbruchFelder, KvpAufgabenZaehler {
  id: number
  projektId: number
  businessId: string | null
  bezeichnung: string
  beschreibung: string | null
  projektleiterEmployeeId: string | null
  start: string | null
  geplantesEnde: string | null
  tatsaechlichesEnde: string | null
  // Ersetzt das manuelle Ninox-Statusfeld (nur noch Import-Historie in der DB)
  abgeleiteterStatus: KvpAbgeleiteterStatus
}

export async function fetchKvpTeilprojekte(projektId?: number): Promise<KvpTeilprojekt[]> {
  const resp = await api.get<KvpTeilprojekt[]>("/kvp/teilprojekte", { params: { projektId } })
  return resp.data
}

export async function createKvpTeilprojekt(payload: {
  projektId: number
  bezeichnung: string
  beschreibung?: string
  projektleiterEmployeeId?: string | null
}): Promise<KvpTeilprojekt> {
  const resp = await api.post<KvpTeilprojekt>("/kvp/teilprojekte", payload)
  return resp.data
}

export async function updateKvpTeilprojekt(
  id: number,
  payload: Partial<{
    bezeichnung: string
    beschreibung: string | null
    projektleiterEmployeeId: string | null
    start: string | null
    geplantesEnde: string | null
    tatsaechlichesEnde: string | null
  }>,
): Promise<KvpTeilprojekt> {
  const resp = await api.put<KvpTeilprojekt>(`/kvp/teilprojekte/${id}`, payload)
  return resp.data
}

// --- Abbruch (terminal, kvp_eskalieren-Kreis): Kaskade auf offene Aufgaben --

export async function bricKvpTeilprojektAb(id: number, grund: string): Promise<KvpTeilprojekt> {
  const resp = await api.post<KvpTeilprojekt>(`/kvp/teilprojekte/${id}/abbrechen`, { grund })
  return resp.data
}

export async function hebeKvpTeilprojektAbbruchAuf(id: number): Promise<KvpTeilprojekt> {
  const resp = await api.post<KvpTeilprojekt>(`/kvp/teilprojekte/${id}/abbruch-aufheben`)
  return resp.data
}

export async function bricKvpProjektAb(id: number, grund: string): Promise<KvpProjekt> {
  const resp = await api.post<KvpProjekt>(`/kvp/projekte/${id}/abbrechen`, { grund })
  return resp.data
}

export async function hebeKvpProjektAbbruchAuf(id: number): Promise<KvpProjekt> {
  const resp = await api.post<KvpProjekt>(`/kvp/projekte/${id}/abbruch-aufheben`)
  return resp.data
}

export interface KvpStatus {
  id: number
  name: string
  farbe: string | null
  icon: string | null
  reihenfolge: number | null
  aktiv: boolean
  istOffen: boolean
  // Ergebnis-Kategorie (Basis der Ebenen-Ableitung); istOffen wird server-
  // seitig konsistent gehalten (istOffen == (kategorie === "offen"))
  kategorie: "offen" | "erledigt" | "verworfen"
}

export async function fetchKvpStatus(nurAktiv = false): Promise<KvpStatus[]> {
  const resp = await api.get<KvpStatus[]>("/kvp/status", { params: { nur_aktiv: nurAktiv } })
  return resp.data
}

/** Pflege der Statusliste (Stammdaten-Seite) — Schreiben nur admin;
 *  istOffen wird serverseitig aus kategorie abgeleitet. */
export type KvpStatusEingabe = Omit<KvpStatus, "id" | "istOffen">

export async function createKvpStatus(payload: KvpStatusEingabe): Promise<KvpStatus> {
  const resp = await api.post<KvpStatus>("/kvp/status", payload)
  return resp.data
}

export async function updateKvpStatus(id: number, payload: KvpStatusEingabe): Promise<KvpStatus> {
  const resp = await api.put<KvpStatus>(`/kvp/status/${id}`, payload)
  return resp.data
}

export interface KvpAufgabe {
  id: number
  teilprojektId: number
  businessId: string | null
  bezeichnung: string
  statusId: number | null
  // Beim kaskadierten Abbruch gesicherter Vorher-Status (Reaktivierungs-Vorschlag)
  statusVorAbbruchId: number | null
  prioritaet: string | null
  fortschrittProzent: number | null
  erstelldatum: string | null
  geplantesEnde: string | null
  tatsaechlichesEnde: string | null
  zeitbedarfStunden: number | null
  spaetesterAnfangstermin: string | null
  zeitverbrauchStunden: number | null
  kosten: number | null
  zustaendigEmployeeId: string | null
  aufgabenstellerEmployeeId: string | null
}

export interface KvpAufgabeFilter {
  teilprojektId?: number
  statusId?: number
  prioritaet?: string
  rolle?: "zustaendig" | "ersteller"
  nurOffene?: boolean
  suche?: string
}

export async function fetchKvpAufgaben(filter?: KvpAufgabeFilter): Promise<KvpAufgabe[]> {
  const resp = await api.get<KvpAufgabe[]>("/kvp/aufgaben", { params: filter })
  return resp.data
}

export async function createKvpAufgabe(payload: {
  teilprojektId: number
  bezeichnung: string
  statusId?: number
  prioritaet?: string
  geplantesEnde?: string
  zeitbedarfStunden?: number
  zustaendigEmployeeId?: string | null
  aufgabenstellerEmployeeId?: string | null
}): Promise<KvpAufgabe> {
  const resp = await api.post<KvpAufgabe>("/kvp/aufgaben", payload)
  return resp.data
}

export async function updateKvpAufgabe(
  id: number,
  payload: Partial<{
    bezeichnung: string
    statusId: number
    prioritaet: string
    fortschrittProzent: number
    zustaendigEmployeeId: string
    aufgabenstellerEmployeeId: string
  }>,
): Promise<KvpAufgabe> {
  const resp = await api.put<KvpAufgabe>(`/kvp/aufgaben/${id}`, payload)
  return resp.data
}

// Kommentar-Thread (kvp_eintraege) abgelöst durch den zentralen
// Berichts-Thread (components/BerichtsThread.tsx + rmnc-nachrichten).

export interface KvpCockpit {
  offeneAufgabenEigene: number
  offeneAufgabenGesamt: number
}

export async function fetchMeinCockpit(): Promise<KvpCockpit> {
  const resp = await api.get<KvpCockpit>("/kvp/cockpit/mich")
  return resp.data
}

export async function eskaliereKvpAufgabe(
  aufgabeId: number,
): Promise<{ ticketId: number; ticketKey: string | null }> {
  const resp = await api.post(`/kvp/aufgaben/${aufgabeId}/eskalieren`)
  return resp.data
}

// E-Mail-Versand abgelöst durch die Checkbox „auch per E-Mail senden" im
// Berichts-Thread; das historische Audit-Log bleibt serverseitig lesbar
// (GET /kvp/aufgaben/{id}/benachrichtigungen).
